import React from 'react';
import { useTranslation } from 'react-i18next';

import type { DeviceMode } from '../../live-preview';
import { Switch, Option } from './DevicePreviewPill.styles';

/* Inline device glyphs (stroke = currentColor, so they take the Option's active/inactive colour). */
const iconProps = {
  width: 17,
  height: 17,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const DesktopGlyph = (): React.ReactElement => (
  <svg {...iconProps}>
    <rect x="2.5" y="3.5" width="19" height="13" rx="2" />
    <path d="M8.5 20.5h7M12 16.5v4" />
  </svg>
);

const TabletGlyph = (): React.ReactElement => (
  <svg {...iconProps}>
    <rect x="5" y="2.5" width="14" height="19" rx="2.2" />
    <path d="M11 18.5h2" />
  </svg>
);

const MobileGlyph = (): React.ReactElement => (
  <svg {...iconProps}>
    <rect x="7" y="2.5" width="10" height="19" rx="2.2" />
    <path d="M11 18.5h2" />
  </svg>
);

const DEVICES: { mode: DeviceMode; Glyph: () => React.ReactElement }[] = [
  { mode: 'desktop', Glyph: DesktopGlyph },
  { mode: 'tablet', Glyph: TabletGlyph },
  { mode: 'mobile', Glyph: MobileGlyph },
];

export type DevicePreviewPillProps = {
  value: DeviceMode;
  onChange: (mode: DeviceMode) => void;
  /** The devices offered, in the pill's order (default: desktop, tablet, mobile). */
  modes?: readonly DeviceMode[];
  /** `touch`: as tall as a Button (48px) with 48px wide options, for a phone's preview head. */
  size?: 'compact' | 'touch';
};

/** Device toggle for a preview header: desktop / tablet / mobile widths (or the `modes` given). */
export const DevicePreviewPill = ({
  value,
  onChange,
  modes,
  size = 'compact',
}: DevicePreviewPillProps): React.ReactElement => {
  const { t } = useTranslation();
  const touch = size === 'touch';
  return (
    <Switch role="group" aria-label={t('Preview device')} $touch={touch}>
      {DEVICES.filter(({ mode }) => !modes || modes.includes(mode)).map(({ mode, Glyph }) => {
        // Literal t() at the render site so the extractor harvests each label (t(variable) is not
        // extractable — see docs/i18n.md).
        const label =
          mode === 'desktop' ? t('Desktop preview') : mode === 'tablet' ? t('Tablet preview') : t('Mobile preview');
        return (
          <Option
            key={mode}
            type="button"
            $active={value === mode}
            $touch={touch}
            aria-pressed={value === mode}
            aria-label={label}
            title={label}
            onClick={() => onChange(mode)}
          >
            <Glyph />
          </Option>
        );
      })}
    </Switch>
  );
};

export default DevicePreviewPill;
